import React, { useState, useEffect, useRef } from 'react'

import ReactCrop, {
  centerCrop,
  makeAspectCrop,
  PercentCrop,
  PixelCrop,
} from 'react-image-crop'

//-- components
import { useStateProps ,useImageProps ,useFormatProps } from '../ImpaasComponents.js';
import ImageLoader from '../ImageLoader.tsx';
import ImageSaver from '../ImageSaver.tsx';
import FormatSelect from '../FormatSelect.tsx';

//-- ui
import { Collapse, InputNumber, Select ,Alert } from "antd";

//-- css
import 'react-image-crop/dist/ReactCrop.css'
import './CropApp.css';

// This is to demonstate how to make and center a % aspect crop
// which is a bit trickier so we use some helper functions.
function centerAspectCrop(
  mediaWidth: number,
  mediaHeight: number,
  aspect: number,
) {
  return centerCrop(
    makeAspectCrop(
      {
        unit: '%',
        width: 90,
      },
      aspect,
      mediaWidth,
      mediaHeight,
    ),
    mediaWidth,
    mediaHeight,
  )
}

//! *** Main Component ***
export default function CropApp(props,ref) {
    // const [imgSrc, setImgSrc] = useState('')
    const imgRef = useRef<HTMLImageElement>(null)
    // const blobUrlRef = useRef('')
    const [crop, setCrop] = useState<PercentCrop>(0,0,100,100) //! will not select anything, but prevent exception

    const previewCanvasRef = useRef<HTMLCanvasElement>(null)
    const hiddenAnchorRef = useRef<HTMLAnchorElement>(null)
    const [completedCrop, setCompletedCrop] = useState<PixelCrop>()

    //-- ImPaaS component
    const { hasStateChanges ,onStateChanges ,isQueuing ,adviseDownload ,adviseQueuing } = useStateProps(props);
    const { noImage ,hasImage ,imageUrl ,imageInfoHeader ,onImageUpload } = useImageProps(props);
    const { formatName ,formatSelect } = useFormatProps(props);

    //-- image
    function onImageLoad(e: React.SyntheticEvent<HTMLImageElement>) {
      if( aspect ) {
        const { width, height } = e.currentTarget
        setCrop(centerAspectCrop(width, height, aspect))
      }

      onStateChanges(false);
    }
  
    // NB intentionnaly left as unused, TODO: need to make proper use of aspect ratio in crop preview
    const [aspect, setAspect] = useState<number|undefined>( undefined ) // 16 / 9

    /* function onSelectFile(e: React.ChangeEvent<HTMLInputElement>) {
      if (e.target.files && e.target.files.length > 0) {
        // setCrop(undefined) // Makes crop preview update between images.

        const reader = new FileReader()
        reader.addEventListener('load', () =>
          setImgSrc(reader.result?.toString() || ''),
        )
        reader.readAsDataURL(e.target.files[0])
      }
    } */

    //--
    const { Panel } = Collapse;
    const { Option } = Select;

    //-- Crop
    function formatCropPercent(value) {
      //! Crop as percent, keep 2 decimal only
      const f = parseFloat(value);

      if( isNaN(f) ) {
        return;
      }

      //! Format the number to two decimal places and remove trailing zeros
      const f2 = f.toFixed(2).replace(/\.00$/, '').replace(/\.0$/, '')
      return `${f2}%`; 
    }

    function parseCropPercent(value) {
      const cleanedValue = value?.replace('%', '');
      return cleanedValue ? parseFloat(cleanedValue) : undefined;
    }

    const onCropChange = (name: string, value: string) => {
      const nameMap = {
        "crop_left": "x",
        "crop_top": "y",
        "crop_width": "width",
        "crop_height": "height",
        "crop_unit": "unit",
      };

      const mname = nameMap[ name ];

      setCrop((prevCrop) => ({
        ...prevCrop,
        [mname]: mname === 'unit' ? value : Number(value), // Convert to number except for 'unit'
      }));
    }
  
    useEffect(() => {
      onStateChanges( hasImage && !!crop && crop.width > 0 && crop.height > 0 );
    }, [hasImage,crop]);

  //--
  return (
    <div className="CropApp">
      <div class="upload-container">
        {!!imageUrl && (
          <>
          <div style={{textAlign: 'left' }}>
            <Alert message={imageInfoHeader} type="info" showIcon />
          </div>              
          <div class="canvas-container">
            <div class="crop-canvas">
              <ReactCrop
                crop={crop}
                onChange={(_, percentCrop) => setCrop(percentCrop)}
                onComplete={(c) => setCompletedCrop(c)}
                aspect={aspect}
                minWidth={16}
                minHeight={16}
                // style={{ backgroundColor: scaleBackground }}
                // circularCrop
              >
                <img
                  ref={imgRef}
                  alt="Crop"
                  src={imageUrl}                 
                  onLoad={onImageLoad}
                />
              </ReactCrop> 
            </div>
          </div>
          </>
        )}
        <hr/>
        <div class="container-fluid d-flex justify-content-between align-items-center">
          <div class="container-fluid d-flex justify-content-center">
            {!isQueuing && (
              <ImageLoader onUpload={onImageUpload}/>
            )}
            {!isQueuing && hasImage && (
              <button disabled={!hasStateChanges} type="button" class="process-button"> Apply </button>
            )}
            <ImageSaver enabled={hasImage} queueTime="5" onDownload={adviseDownload} onQueue={adviseQueuing}/>
          </div>
        </div>
      </div>
      
      <div class="method-group">
        <ul class="property-list">
          <Collapse defaultActiveKey={['1','2']}>
            <Panel header={<h6>Crop</h6>} key="1" className="property-panel">
              <label>Left:</label>
              <InputNumber<number>
                name="crop_left" min={0} max={100} step={1} 
                disabled={noImage} value={crop.x} onChange={(value) => onCropChange("crop_left",value)}
                formatter={(value) => formatCropPercent(value)}
                parser={(value) => parseCropPercent(value)}
              />
              <label>Top:</label>
              <InputNumber<number>
                name="crop_top" min={0} max={100} step={1} 
                disabled={noImage} value={crop.y} onChange={(value) => onCropChange("crop_top",value)}
                formatter={(value) => formatCropPercent(value)}
                parser={(value) => parseCropPercent(value)}
              />
              <label>Width:</label>
              <InputNumber<number>
                name="crop_width" min={0} max={100} step={1} 
                disabled={noImage} value={crop.width} onChange={(value) => onCropChange("crop_width",value)}
                formatter={(value) => formatCropPercent(value)}
                parser={(value) => parseCropPercent(value)}
              />
              <label>Height:</label>
              <InputNumber<number>
                name="crop_height" min={0} max={100} step={1} 
                disabled={noImage} value={crop.height} onChange={(value) => onCropChange("crop_height",value)}
                formatter={(value) => formatCropPercent(value)}
                parser={(value) => parseCropPercent(value)}
              />

              <label>Unit:</label> 
              <Select disabled={noImage} name="crop_unit" value="Percent" style={{ width: 100 }}>
                <Option value="Pixels">Pixels</Option>
                <Option value="Percent">Percent</Option>
              </Select>

            </Panel>
            <Panel header={<h6>Format</h6>} key="2" className="property-panel">
              <FormatSelect enabled={hasImage} name={formatName} onSelect={formatSelect} />
            </Panel>
          </Collapse>         
        </ul>      
      </div>
    </div>
  )
}
